import { useEffect, useRef, useState } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { activeFilterCount } from "@/lib/filters/apply.ts";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { filterPermalink, flushFilterUrl, resetFilters, useFilters } from "@/state/filters.ts";
import { formatMinutes } from "../entry/RuntimeChip.tsx";
import styles from "./controls.module.css";

const FLASH_MS = 2400;

/**
 * What the panel adds up to: how many controls are doing something, what
 * that leaves you with, and the two things you do next — start over, or send
 * it to someone.
 *
 * Share copies the permalink rather than the address bar's current value: the
 * URL write is debounced, so it flushes first and then copies the state it
 * just wrote. Nobody ever ships a link that's one keystroke stale.
 */
export function FilterFooter() {
  const catalog = useCatalog();
  const filters = useFilters();
  const { entries, minutes, essentialsVisible, essentialsTotal } = useFilteredRoad();
  const [flash, setFlash] = useState<"copied" | "failed" | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimer.current !== null) clearTimeout(flashTimer.current);
    },
    [],
  );

  const active = activeFilterCount(filters);

  const share = async () => {
    flushFilterUrl();
    const url = filterPermalink(filters);
    try {
      await navigator.clipboard.writeText(url);
      setFlash("copied");
    } catch {
      setFlash("failed");
    }
    if (flashTimer.current !== null) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), FLASH_MS);
  };

  return (
    <div className={styles.footer}>
      <p className={styles.tally}>
        <strong>
          {entries.length} of {catalog.totals.entryCount}
        </strong>{" "}
        {catalog.project.vocabulary.stops} · {formatMinutes(minutes)}
        <br />
        <span className={styles.tallyMuted}>
          {active === 0 ? "No filters active" : `${active} ${active === 1 ? "filter" : "filters"}`}
          {" · "}
          {essentialsVisible} of {essentialsTotal} essentials
        </span>
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          onClick={resetFilters}
          disabled={active === 0}
        >
          Reset
        </button>
        <button type="button" className={`${styles.button} ${styles.primary}`} onClick={share}>
          Share
        </button>
      </div>
      <p className={styles.flash} role="status" aria-live="polite">
        {flash === "copied" && "Link copied. It carries these exact filters."}
        {flash === "failed" && "Couldn't copy; the address bar has the link."}
      </p>
    </div>
  );
}
